<template>
  <chat-csat-popup
    :visible="visible"
    :loading="loading"
    :error="failed"
    :submitted="submitted"
    @submit="onSubmit"
    @close="onDismiss"
  />
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import CashflowAssistantApi from '@/api/CashflowAssistant';
import DDLogs from '@/DDLogs';
import Segment from '@/Segment';
import { AI_CSAT_SOURCE, CSAT_POPUP_CONFIRMATION_DURATION_MS } from '@/constants/chat';
import {
  clearCandidate, clearCandidateIn, eligibleCandidate, escalatedConversationIds, markHandled,
} from './aiChatCsat';
import ChatCsatPopup from './ChatCsatPopup.vue';

// Lives on the app frame (next to CxMessageToast) so the popup can appear once
// the chat sheet is gone: right when it closes, or on the next app open when the
// app was closed instead. Chat close is read from the riseupChat store (chatOpen).
export default {
  name: 'AiChatCsatHost',
  components: { ChatCsatPopup },
  data() {
    return {
      visible: false,
      loading: false,
      failed: false,
      submitted: false,
      conversationId: null,
      closeTimer: null,
      checking: false,
      disposed: false,
    };
  },
  computed: {
    ...mapGetters('featureFlags', { enabled: 'aiChatCsat' }),
    ...mapState('riseupChat', ['chatOpen']),
  },
  watch: {
    // Flags load after the frame mounts, so a candidate left from the previous
    // session is checked again once the flag is known.
    enabled(isOn) {
      if (isOn) {
        this.tryShow();
      }
    },
    // Chat sheet closed.
    chatOpen(isOpen, wasOpen) {
      if (wasOpen && !isOpen) {
        this.tryShow();
      }
    },
  },
  mounted() {
    this.tryShow();
  },
  beforeDestroy() {
    this.disposed = true;
    clearTimeout(this.closeTimer);
  },
  methods: {
    async tryShow() {
      if (!this.enabled || this.visible || this.chatOpen || this.checking) {
        return;
      }
      const pending = eligibleCandidate();
      if (!pending) {
        return;
      }
      // The thread may have been handed to the team on another device. Chatbot
      // withdraws those from its own fetch, but it only mounts once the chat is
      // opened, so the popup has to ask the server itself before showing.
      this.checking = true;
      try {
        const data = await CashflowAssistantApi.getConversations();
        clearCandidateIn(escalatedConversationIds(data && data.conversations));
      } catch (error) {
        // Unknown state: leave the candidate pending rather than risk asking
        // about a thread the team owns. Retried on the next close.
        DDLogs.error('[ai-csat] Could not check the conversation before asking', {
          conversationId: pending.conversationId,
        });
        return;
      } finally {
        this.checking = false;
      }
      // Re-read: withdrawn just now, or the state moved while this was in flight.
      const candidate = eligibleCandidate();
      if (!candidate || this.disposed || !this.enabled || this.visible || this.chatOpen) {
        return;
      }
      this.conversationId = candidate.conversationId;
      // Consumed on show, so closing the app with the popup open cannot re-ask.
      clearCandidate(candidate.conversationId);
      this.failed = false;
      this.submitted = false;
      this.visible = true;
      Segment.trackUserInteraction('AIChatCsatShown', { conversationId: candidate.conversationId });
    },
    async onSubmit({ score, comment }) {
      if (this.loading) {
        return;
      }
      this.loading = true;
      this.failed = false;
      try {
        await CashflowAssistantApi.submitAiChatCsat(this.conversationId, score, comment || '');
        markHandled(this.conversationId);
        // Tracked after the save so a retry after a failure counts once.
        Segment.trackUserInteraction('CSATSurveyAnsweredClicked', {
          surveyAnswers: { CSATScore: score, CSATExperience: comment || null },
          conversationId: this.conversationId,
          source: AI_CSAT_SOURCE,
        });
        this.submitted = true;
        this.closeTimer = setTimeout(() => {
          this.visible = false;
          // A chat closed while the thank-you card was up was skipped by tryShow;
          // retried after the hide has flushed so the card actually leaves first.
          this.$nextTick(this.tryShow);
        }, CSAT_POPUP_CONFIRMATION_DURATION_MS);
      } catch (error) {
        const status = error && error.response && error.response.status;
        DDLogs.error('[ai-csat] Failed to submit the AI chat rating', { conversationId: this.conversationId, status });
        // Session gone: nothing the customer can retry here.
        if (status === 403) {
          this.visible = false;
          return;
        }
        // Keep the popup open with the customer's input; the popup shows why.
        this.failed = true;
      } finally {
        this.loading = false;
      }
    },
    onDismiss() {
      markHandled(this.conversationId);
      Segment.trackUserInteraction('AIChatCsatDismissed', { conversationId: this.conversationId });
      this.visible = false;
    },
  },
};
</script>
